import React, { ComponentProps, useCallback, useContext } from 'react'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { connect } from 'react-redux'
import PriceRowLoyaltyPoints from 'components/Luxkit/PricePoints/Loyalty/PriceRowLoyaltyPoints'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import { useInView } from 'react-intersection-observer'
import useLoyaltyPointsEarnCalculator from 'loyalty/hooks/useLoyaltyPointsEarnCalculator'
import PriceRowLoyaltyPointsLoadingSkeleton from 'components/Luxkit/PricePoints/Loyalty/PriceRowLoyaltyPointsLoadingSkeleton'
import { nonNullable } from 'lib/array/arrayUtils'
import ModalContext from 'contexts/ModalContext'
import LoyaltyGenericInfoModal from './LoyaltyGenericInfoModal'

interface MappedProps {
  loyaltyEnabled: boolean;
  displayTier: App.LoyaltyTier;
}

type InheritedPriceRowLoyaltyPointsProps = Pick<ComponentProps<typeof PriceRowLoyaltyPoints>, 'onInfoIconClick' | 'calculationType'>

interface Props extends MappedProps, InheritedPriceRowLoyaltyPointsProps {
  calculationRequests: Array<App.LoyaltyPointsEarnCalculationRequest | undefined> | App.LoyaltyPointsEarnCalculationRequest | undefined;
  /**
   * @default 'S'
   */
  size?: React.ComponentProps<typeof PriceRowLoyaltyPoints>['size'];
  /**
   * @default false
   */
  hideStatusCredits?: boolean;
}

function LoyaltyPoints(props: Props) {
  const {
    loyaltyEnabled,
    calculationRequests,
    size = 'S',
    hideStatusCredits = false,
    calculationType,
    displayTier,
    onInfoIconClick: onInfoIconClickOverride,
  } = props

  const showModal = useContext(ModalContext)

  const defaultIconClickAction = useCallback(() => {
    showModal(<LoyaltyGenericInfoModal />)
  }, [showModal])

  const onInfoIconClick = onInfoIconClickOverride ?? defaultIconClickAction

  const [loyaltyPointsRef, loyaltyPointsInView] = useInView({
    triggerOnce: true,
    rootMargin: '400px 0px 400px 0px',
  })

  const { totalPointsCalculation } = useLoyaltyPointsEarnCalculator(calculationRequests, {
    skipAll: !loyaltyPointsInView,
  })

  const initial = totalPointsCalculation?.initial
  const fetching = !!totalPointsCalculation?.fetching
  const err = totalPointsCalculation?.error
  const points = totalPointsCalculation?.data?.points ?? 0
  const statusCredits = totalPointsCalculation?.data?.statusCredits ?? 0

  if (
    !loyaltyEnabled ||
    !calculationRequests ||
    (Array.isArray(calculationRequests) && nonNullable(calculationRequests).length === 0) ||
    err ||
    (!initial && !fetching && !points)) return null

  return <div ref={loyaltyPointsRef}>
    {fetching && <PriceRowLoyaltyPointsLoadingSkeleton tier={displayTier} size={size} />}
    {!fetching && <PriceRowLoyaltyPoints
      tier={displayTier}
      earnablePoints={points}
      size={size}
      calculationType={calculationType}
      statusCredits={hideStatusCredits ? undefined : statusCredits}
      onInfoIconClick={onInfoIconClick}
    />}
  </div>
}

function mapStateToProps(state: App.State): MappedProps {
  return ({
    loyaltyEnabled: getIsLoyaltyEnabled(state),
    displayTier: getLoyaltyDisplayTier(state),
  })
}

export default connect(mapStateToProps)(LoyaltyPoints)
